import { queryOptions, useQueries, useQuery, UseQueryResult } from '@tanstack/react-query';

import { useFetchUserData } from '@/modules/Auth/hooks/useFetchUserData.ts';
import { AssetsAPINamespace } from '@/shared/api/assets/assets.requests.ts';
import { useCategoryContext } from '@/shared/context/category-ctree.tsx';
import { QUERY_SCHEMA } from '@/shared/schemas/query-schemas.ts';
import { useGlobalStore } from '@/shared/store/store.ts';
import { QUERY_KEY, REQUESTS_OPTIONS } from '@/shared/utils/constants.ts';

import { AssetsParams, CombineResultsProps } from '@/shared/api/assets/assets.types.ts';
import { AssetBody } from '@/shared/types/assets/assets.types.ts';

export namespace useAssetHooks {
  export const getAllEquipments = () => {
    const { getCategoriesByType } = useCategoryContext();
    const site = useGlobalStore(store => store.site);
    const { user } = useFetchUserData();

    const assetsCategories = getCategoriesByType('Equipment');
    const categories: string[] = assetsCategories.map(item => item.categoryTreeResName);

    return useQueries({
      combine: (results: UseQueryResult<AssetBody[], Error>[]): CombineResultsProps => {
        const [response] = results;

        return {
          data: response?.data?.map(equipment => ({ ...equipment, visible: true })) ?? [],
          isError: results.some(result => result.isError),
          isFetching: results.some(result => result.isFetching),
          isLoading: results.length > 0 ? results.some(result => result.isLoading) : true,
          isPending: results.some(result => result.isPending),
          isSuccess: results.every(result => result.isSuccess),
          refetch: () => results.forEach(result => result.refetch()),
        };
      },

      queries: categories.map(category => ({
        enabled: !!user?.accountResourceName && !!site,

        queryFn: async () => {
          const params: AssetsParams = {
            categoryTreeResName: category,
            includeTags: true,
            limit: REQUESTS_OPTIONS.LIMIT_POINTS,
            statuses: ['active', 'inactive'],
          };

          return await AssetsAPINamespace.getAll({
            accessToken: user?.accessToken,
            accountResName: user?.accountResourceName as string,
            params,
            siteResName: site,
          });
        },

        queryKey: [QUERY_KEY.EQUIPMENTS_GET, category, user, site],
      })),
    });
  };

  export const getAllWorkers = () => {
    const { getCategoriesByType, getCategoryByType } = useCategoryContext();
    const site = useGlobalStore(store => store.site);
    const { user } = useFetchUserData();

    const assetsCategories = getCategoriesByType('Worker');
    const currentCategory = getCategoryByType('Worker')?.categoryTreeResName;

    const workerQueryOptions = queryOptions({
      enabled: !!user?.accountResourceName && !!site && assetsCategories.length > 0,

      queryFn: async () => {
        const params: AssetsParams = {
          categoryTreeResName: currentCategory,
          includeTags: true,
          limit: REQUESTS_OPTIONS.LIMIT_POINTS,
          statuses: ['active', 'inactive'],
        };

        return await AssetsAPINamespace.getAll({
          accessToken: user?.accessToken,
          accountResName: user?.accountResourceName as string,
          params,
          siteResName: site,
        });
      },

      queryKey: [QUERY_KEY.WORKERS, user, site, currentCategory],

      select: data => {
        return data.map(worker => ({ ...worker, visible: true }));
      },
    });

    return useQuery(workerQueryOptions);
  };

  export const getCategoryTrees = () => {
    const site = useGlobalStore(state => state.site);
    const { user } = useFetchUserData();

    const assetsCategoriesQueryOptions = queryOptions({
      enabled: !!user?.accountResourceName && !!site && !!user?.accessToken,

      queryFn: async () => {
        return await AssetsAPINamespace.getAllCategoryTrees(
          user?.accountResourceName as string,
          site,
          user?.accessToken as string,
        );
      },

      queryKey: [QUERY_KEY.ASSETS_CATEGORIES, user, site],

      select: data => {
        return data.map(item => {
          const modifiedItemPaths = item.categoryPathList.map(pathItem => {
            return {
              ...pathItem,
              extended: false,
            };
          });

          return {
            ...item,
            categoryPathList: modifiedItemPaths,
            extended: true,
          };
        });
      },

      staleTime: QUERY_SCHEMA.STALE_TIME.DAY,
    });

    return useQuery(assetsCategoriesQueryOptions);
  };
}
